import uniq from "lodash/uniq";

import {
  AccuracyAggregate,
  accuracyAggregatePseudoClone,
  AccuracyDetail,
  accuracyDetailPseudoClone,
  AccuracyService,
  buildAccuracyAggregate,
  buildAccuracyDetail,
} from "@app/projects/network-assessment/accuracy";

import {
  AdequacyAggregate,
  adequacyAggregatePsuedoClone,
  AdequacyDetail,
  adequacyDetailPsuedoClone,
  buildAdequacyAggregate,
  buildAdequacyDetail,
} from "@app/projects/network-assessment/adequacy";
import { AccuracyTrendChartItem, AdequacyTrendChartItem, AnalysisSpecialtyGroup, ResultRegion, SpecialtyGroupException } from "@app/projects/results/common";
import { CompositeRegionSetAggregate, CompositeSpecialtyGroupAggregate, CompositeSummarySpecialtyGroup } from "@app/projects/results/composite";
import { RegionGranularity, RegionSet } from "@app/region-sets";
import { RegionSetQualifier } from "@app/region-sets";
import { TemplateMetadata, TemplateService } from "@app/templates";
import { ProjectOptions, ProjectService } from "@app/projects";
import { AccuracyGapAnalysisStatus, NetworkAssessmentAnalysisType, ProjectExclusionType, ProjectRunConfigurationStatus, ProjectRunConfigurationType, RegionSetStatusType, TimeDistanceCalculatorType, TimeFrequency } from "@app/projects/enumerators";
import { AggregateDisplayType, NetworkType } from "@app/projects/literals";
import {
  AttestationAggregate,
  AttestationDetail,
  AttestationService,
  buildAttestationAggregate,
} from "@app/projects/network-assessment/attestation";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";

import { AccuracyAggregateInspectionResult, AccuracyAggregateInspectionResultGroup, AccuracyDetailsBatch, AccuracyProjectResults, AccuracyViewByRegionResponse } from "@app/projects/results/accuracy";
import { GeographyService } from "@app/geography";
import AvailableRegionStatusesModel from "@app/projects/availableRegionStatusesModel";

import { Dictionary, flatMap } from "lodash";
import { SortDirection } from "@app/shared/griddle/enumerators";
import {
  buildCompositeAggregate,
  buildCompositeDetail,
  CompositeAggregate,
  compositeAggregatePseudoClone,
  CompositeDetail,
  compositeDetailPseudoClone,
} from "@app/projects/network-assessment/composite";
import { AccuracySpecialtyGroupAggregate } from "@app/projects/results/accuracy";
import { AdequacyProjectResults } from "@app/projects/results/adequacy";
import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";
import { PciDashboardStatusEnum } from "@app/projects/network-assessment/pci-dashboard/shared";

interface AggregateOptions {
  specialtyGroup?: AnalysisSpecialtyGroup,
  aggregateDisplayType?: AggregateDisplayType,
  inspectionUid?: string,
  bandFilter?: string,
  specialtyGroupAggregates?: AccuracySpecialtyGroupAggregate[]
}

interface ProjectRunConfigurationResult extends ProjectRunConfiguration {
  readonly networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  readonly networkTypes: ReadonlySet<NetworkType>;
  readonly serviceArea: ResultRegion;
  readonly specialtyGroups: AnalysisSpecialtyGroup[];
  readonly template: TemplateMetadata;

  regionSpecialtyGroupExceptions: { [regionKey: string]: SpecialtyGroupException };

  states: () => ResultRegion[];
  CBSAs: () => ResultRegion[];
  counties: () => ResultRegion[];
  countyZips: () => ResultRegion[];
  zips: () => ResultRegion[];

  getAvailableRegionStatusTypes: (resultRegion: ResultRegion) => RegionSetStatusType[];
  getState: (regionCode: string) => ResultRegion;
  getCBSA: (regionCode: string) => ResultRegion;
  getCounty: (regionCode: string) => ResultRegion;
  getCountyZip: (regionCode: string, subRegionCode: string) => ResultRegion;
  getZip: (regionCode: string) => ResultRegion;

  getRegionSetAdequacyAggregate(resultRegion: ResultRegion): Promise<AdequacyAggregate>;
  getRegionSetAdequacyAggregates(resultRegions: ResultRegion[]): Promise<AdequacyAggregate[]>;
  getRegionSetSpecialtyGroupAdequacyAggregate(resultRegion: ResultRegion, specialtyGroup: AnalysisSpecialtyGroup): Promise<AdequacyAggregate>;
  getRegionSetSpecialtyGroupAdequacyAggregates(resultRegion: ResultRegion): Promise<AdequacyAggregate[]>;
  getBulkRegionSetSpecialtyGroupAdequacyAggregates(resultRegions: ResultRegion[], specialtyGroup: AnalysisSpecialtyGroup): Promise<AdequacyAggregate[]>;
  getBulkRegionSetSpecialtyGroupAccuracyAggregates(resultRegions: ResultRegion[], specialtyGroup: AnalysisSpecialtyGroup): Promise<AccuracyAggregate[]>;

  clearAdequacyDetails(resultRegion: ResultRegion): void;
  getAdequacyDetails(resultRegion: ResultRegion, specialtyGroup?: AnalysisSpecialtyGroup, aggregateDisplayType?: AggregateDisplayType): Promise<AdequacyDetail[]>;

  getAccuracyRegionAggregates(resultRegions: ResultRegion[], options?: AggregateOptions, useLargeProjectEnhancements?: boolean): Promise<AccuracyAggregate[]>;
  getAccuracyAggregate(resultRegion: ResultRegion, options?: AggregateOptions): Promise<AccuracyAggregate>;
  getAccuracyDetails(resultRegion: ResultRegion, SpecialtyGroup?: AnalysisSpecialtyGroup, analysisDisplayType?: AggregateDisplayType): Promise<AccuracyDetail[]>;
  getBatchedAccuracyDetails(resultRegions: ResultRegion[], skip?: number, take?: number, sortDirection?: SortDirection, selectedColumn?: string, sortColor?: string): Promise<AccuracyDetailsBatch>;

  getCompositeAggregate(resultRegion: ResultRegion, specialtyGroup?: AnalysisSpecialtyGroup, aggregateDisplayType?: AggregateDisplayType): Promise<CompositeAggregate>;
  getCompositeDetails(resultRegion: ResultRegion, specialtyGroup?: AnalysisSpecialtyGroup, aggregateDisplayType?: AggregateDisplayType): Promise<CompositeDetail[]>;

  getAccuracyTrendChartData(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, specialtyGroup?: AnalysisSpecialtyGroup, inspection?: string): Promise<AccuracyTrendChartItem[]>;
  getAdequacyTrendChartData(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, specialtyGroup?: AnalysisSpecialtyGroup): Promise<AdequacyTrendChartItem[]>;
  getTrendChartDataDualMembership(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, runConfigurationType: ProjectRunConfigurationType, specialtyGroup?: AnalysisSpecialtyGroup): Promise<AdequacyTrendChartItem[]>;

  setException(exception: SpecialtyGroupException): Promise<[string, SpecialtyGroupException]>;
  setStatus(region: RegionSetQualifier, newStatus: RegionSetStatusType): Promise<[AccuracyAggregate, AdequacyAggregate, AttestationAggregate]>;
}

class ProjectRunConfigurationResultImpl implements ProjectRunConfigurationResult {
  public projectRunConfigurationId: number;
  public projectRunConfigurationType: ProjectRunConfigurationType;
  public companionProjectRunConfigurationId?: number;
  public memberSetAlias: string;
  public memberSetId: number;
  public memberSetVersionId: number;
  public networkProviderSetAlias?: string;
  public networkProviderSetId: number;
  public useMarketProvidersForImpact: boolean;
  public accuracyTemplateCollectionId: string;
  public accuracyTemplateCorrelationId: string;
  public accuracyTemplateId: string;
  public adequacyTemplateCollectionId: number;
  public adequacyTemplateCorrelationId: string;
  public adequacyTemplateId: string;
  public serviceAreaRegionSetId: number;
  public autoArchiveUnarchiveSpecialtyExceptions: boolean;
  public exclusions: ProjectExclusionType[];
  public excludedSpecialtyGroupCodes: string[];
  public resultGranularities: RegionGranularity[];
  public dateCreated: Date;
  public datePublished?: Date;
  public publishComment: string;
  public prospectProviderSetAlias?: string;
  public prospectProviderSetId?: number;
  public prospectProviderSetVersionId?: number;
  public marketProviderSetAlias?: string;
  public marketProviderSetId?: number;
  public marketProviderSetVersionId?: number;
  public status: ProjectRunConfigurationStatus;
  public accuracyGapAnalysisStatus: AccuracyGapAnalysisStatus;
  public accessCodeMapSetId?: number;
  public accessCodeMapSetVersionId?: number;
  public hasServiceAreaRegions: boolean;
  public hasOpportunityRegions: boolean;
  public hasUnprocessedSpecialtyGroupExceptions: boolean;
  public inspectedInsurances: string;
  public isLatestPublished: boolean;
  public isTermed: boolean;
  public isProjected: boolean;
  public isProjectedTermed: boolean;
  public hasProjectionResults: boolean;
  public recentAttestationDays: number;
  public telehealthSpecialtyGroupCodes: string[];
  public hasConCredits: boolean;
  public timeDistanceCalculatorType: TimeDistanceCalculatorType;

  public marketNetworkName: string;
  public memberSetName: string;
  public prospectNetworkName: string;
  public providerNetworkName: string;
  public templateName: string;
  public showAccuracyGapAnalysis: boolean;
  public showDistance: boolean;
  public showTime: boolean;
  public readonly showWithTimeAndDistanceColumns: boolean;
  public showNppesDates: boolean;
  public showNextClosestNetworkProviders: boolean;

  public hasZipSummaries: boolean;
  public showCMSCredit: boolean;
  public showCareJourneyScores: boolean;
  public showMipsScore: boolean;
  public showPciData: boolean;
  public showLocationConfidenceScores: boolean;
  public showVaClaimsVolumeColumn: boolean;
  public showTricareClaimsVolumeColumn: boolean;
  public ignoreTimeAndDistanceWithWithoutValues: boolean;
  public pciDashboardStatus: PciDashboardStatusEnum;
  public includeInEnterpriseComplianceDashboard: boolean;
  public showPciDashboard: boolean;
  public showMotiveAppropriatenessScore: boolean;
  public showNetworkIntegrityDashboard: boolean;
  public showAffiliationsInsights: boolean;
  public displayOnNetworkAccordion: boolean;

  public excludeProviderClaimsActivityInspector: boolean;
  public excludeAccuracyPractitionerAtLocationInspector: boolean;
  public calculateStandards: boolean;

  public useNetworkProvidersForMarketAnalysis?: boolean;
  public useProspectProvidersForMarketAnalysis?: boolean;

  private accuracyDetails: { [regionKey: string]: { [regionKey: string]: AccuracyDetail } } = {};
  private adequacyDetails: { [regionKey: string]: { [regionKey: string]: AdequacyDetail } } = {};
  private attestationDetails: { [regionKey: string]: { [regionKey: string]: AttestationDetail } } = {};
  private compositeDetails: { [regionKey: string]: { [regionKey: string]: CompositeDetail } } = {};
  private adequacyTrendChartData: { [key: string]: AdequacyTrendChartItem[] } = {};
  private accuracyTrendChartData: { [key: string]: AccuracyTrendChartItem[] } = {};

  public constructor(
    public readonly networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    private accuracyAggregates: { [regionKey: string]: AccuracyAggregate },
    private adequacyAggregates: { [regionKey: string]: AdequacyAggregate },
    private attestationAggregates: { [regionKey: string]: AttestationAggregate },
    private availableRegionStatuses: { [regionSetId: string]: AvailableRegionStatusesModel },
    private regionStatuses: { [regionKey: string]: RegionSetStatusType },
    public regionSpecialtyGroupExceptions: { [regionKey: string]: SpecialtyGroupException },
    public readonly serviceArea: ResultRegion,
    public readonly specialtyGroups: AnalysisSpecialtyGroup[],
    private projectId: number,
    private projectService: ProjectService,
    private specialtyGroupExceptionsService: SpecialtyGroupExceptionsService,
    private accuracyService: AccuracyService,
    private attestationService: AttestationService,
    public readonly template: TemplateMetadata,
    projectRunConfiguration: ProjectRunConfiguration,
    public networkTypes: ReadonlySet<NetworkType>,
  ) {
    this.projectRunConfigurationId = projectRunConfiguration.projectRunConfigurationId;
    this.projectRunConfigurationType = projectRunConfiguration.projectRunConfigurationType;
    this.companionProjectRunConfigurationId = projectRunConfiguration.companionProjectRunConfigurationId;
    this.memberSetAlias = projectRunConfiguration.memberSetAlias;
    this.memberSetId = projectRunConfiguration.memberSetId;
    this.memberSetVersionId = projectRunConfiguration.memberSetVersionId;
    this.memberSetVersionId = projectRunConfiguration.memberSetVersionId;
    this.networkProviderSetAlias = projectRunConfiguration.networkProviderSetAlias;
    this.networkProviderSetId = projectRunConfiguration.networkProviderSetId;
    this.useMarketProvidersForImpact = projectRunConfiguration.useMarketProvidersForImpact;
    this.accuracyTemplateCollectionId = projectRunConfiguration.accuracyTemplateCollectionId;
    this.accuracyTemplateCorrelationId = projectRunConfiguration.accuracyTemplateCorrelationId;
    this.accuracyTemplateId = projectRunConfiguration.accuracyTemplateId;
    this.adequacyTemplateCollectionId = projectRunConfiguration.adequacyTemplateCollectionId;
    this.adequacyTemplateCorrelationId = projectRunConfiguration.adequacyTemplateCorrelationId;
    this.adequacyTemplateId = projectRunConfiguration.adequacyTemplateId;
    this.serviceAreaRegionSetId = projectRunConfiguration.serviceAreaRegionSetId;
    this.autoArchiveUnarchiveSpecialtyExceptions = projectRunConfiguration.autoArchiveUnarchiveSpecialtyExceptions;
    this.exclusions = projectRunConfiguration.exclusions;
    this.excludedSpecialtyGroupCodes = projectRunConfiguration.excludedSpecialtyGroupCodes;
    this.resultGranularities = projectRunConfiguration.resultGranularities;
    this.inspectedInsurances = projectRunConfiguration.inspectedInsurances;
    this.dateCreated = projectRunConfiguration.dateCreated;
    this.datePublished = projectRunConfiguration.datePublished;
    this.prospectProviderSetAlias = projectRunConfiguration.prospectProviderSetAlias;
    this.prospectProviderSetId = projectRunConfiguration.prospectProviderSetId;
    this.prospectProviderSetVersionId = projectRunConfiguration.prospectProviderSetVersionId;
    this.publishComment = projectRunConfiguration.publishComment;
    this.marketProviderSetAlias = projectRunConfiguration.marketProviderSetAlias;
    this.marketProviderSetId = projectRunConfiguration.marketProviderSetId;
    this.marketProviderSetVersionId = projectRunConfiguration.marketProviderSetVersionId;
    this.status = projectRunConfiguration.status;
    this.accuracyGapAnalysisStatus = projectRunConfiguration.accuracyGapAnalysisStatus;
    this.accessCodeMapSetId = projectRunConfiguration.accessCodeMapSetId;
    this.accessCodeMapSetVersionId = projectRunConfiguration.accessCodeMapSetVersionId;
    this.hasServiceAreaRegions = projectRunConfiguration.hasServiceAreaRegions;
    this.hasOpportunityRegions = projectRunConfiguration.hasOpportunityRegions;
    this.hasUnprocessedSpecialtyGroupExceptions = projectRunConfiguration.hasUnprocessedSpecialtyGroupExceptions;
    this.isLatestPublished = projectRunConfiguration.isLatestPublished;
    this.isTermed = projectRunConfiguration.isTermed;
    this.isProjected = projectRunConfiguration.isProjected;
    this.isProjectedTermed = projectRunConfiguration.isProjectedTermed;
    this.hasProjectionResults = projectRunConfiguration.hasProjectionResults;
    this.recentAttestationDays = projectRunConfiguration.recentAttestationDays;
    this.telehealthSpecialtyGroupCodes = projectRunConfiguration.telehealthSpecialtyGroupCodes || [];
    this.hasConCredits = projectRunConfiguration.hasConCredits;
    this.timeDistanceCalculatorType = projectRunConfiguration.timeDistanceCalculatorType;

    this.providerNetworkName = projectRunConfiguration.providerNetworkName;
    this.prospectNetworkName = projectRunConfiguration.prospectNetworkName;
    this.marketNetworkName = projectRunConfiguration.marketNetworkName;
    this.templateName = projectRunConfiguration.templateName;
    this.memberSetName = projectRunConfiguration.memberSetName;
    this.showAccuracyGapAnalysis = projectRunConfiguration.showAccuracyGapAnalysis;
    this.showDistance = projectRunConfiguration.showDistance;
    this.showTime = projectRunConfiguration.showTime;
    this.showWithTimeAndDistanceColumns = projectRunConfiguration.showWithTimeAndDistanceColumns;
    this.showNppesDates = projectRunConfiguration.showNppesDates;

    this.hasZipSummaries = projectRunConfiguration.hasZipSummaries;
    this.showNextClosestNetworkProviders = projectRunConfiguration.showNextClosestNetworkProviders;
    this.showMipsScore = projectRunConfiguration.showMipsScore;
    this.showCMSCredit = projectRunConfiguration.showCMSCredit;
    this.showPciData = projectRunConfiguration.showPciData;
    this.showLocationConfidenceScores = projectRunConfiguration.showLocationConfidenceScores;
    this.showVaClaimsVolumeColumn = projectRunConfiguration.showVaClaimsVolumeColumn;
    this.showTricareClaimsVolumeColumn = projectRunConfiguration.showTricareClaimsVolumeColumn;
    this.ignoreTimeAndDistanceWithWithoutValues = projectRunConfiguration.ignoreTimeAndDistanceWithWithoutValues;
    this.pciDashboardStatus = projectRunConfiguration.pciDashboardStatus;
    this.includeInEnterpriseComplianceDashboard = projectRunConfiguration.includeInEnterpriseComplianceDashboard;
    this.showPciDashboard = projectRunConfiguration.showPciDashboard;
    this.showMotiveAppropriatenessScore = projectRunConfiguration.showMotiveAppropriatenessScore;
    this.showAffiliationsInsights = projectRunConfiguration.showAffiliationsInsights;
    this.displayOnNetworkAccordion = projectRunConfiguration.displayOnNetworkAccordion;

    this.excludeProviderClaimsActivityInspector = projectRunConfiguration.excludeProviderClaimsActivityInspector;
    this.excludeAccuracyPractitionerAtLocationInspector = projectRunConfiguration.excludeAccuracyPractitionerAtLocationInspector;

    this.useNetworkProvidersForMarketAnalysis = projectRunConfiguration.useNetworkProvidersForMarketAnalysis;
    this.useProspectProvidersForMarketAnalysis = projectRunConfiguration.useProspectProvidersForMarketAnalysis;

    this.calculateStandards = projectRunConfiguration.calculateStandards;
    this.showCareJourneyScores = projectRunConfiguration.showCareJourneyScores;
  }

  public states(): ResultRegion[] {
    return this.serviceArea && this.serviceArea.subRegions
      ? this.serviceArea.subRegions.filter((sr) => sr.regionGranularity === RegionGranularity.State)
      : null;
  }

  public CBSAs(): ResultRegion[] {
    return this.serviceArea && this.serviceArea.subRegions
      ? this.serviceArea.subRegions.filter((sr) => sr.regionGranularity === RegionGranularity.CBSA)
      : null;
  }
  public counties(): ResultRegion[] {
    return this.serviceArea && this.serviceArea.subRegions
      ? (this.serviceArea.subRegions.filter((sr) => sr.regionGranularity === RegionGranularity.State || sr.regionGranularity === RegionGranularity.CBSA))
        .map((sr) => sr.subRegions.filter((ssr) => ssr.regionGranularity === RegionGranularity.County))
        .reduce((acc, regions) => [...acc, ...regions], [])
      : null;
  }
  public countyZips(): ResultRegion[] {
    return this.serviceArea && this.serviceArea.subRegions
      ? this.counties()
        .map((sr) => sr.subRegions.filter((ssr) => ssr.regionGranularity === RegionGranularity.CountyZip))
        .reduce((acc, regions) => [...acc, ...regions], [])
      : null;
  }
  public zips(): ResultRegion[] {
    return this.serviceArea && this.serviceArea.subRegions
      ? this.states()
        .map((sr) => sr.subRegions.filter((ssr) => ssr.regionGranularity === RegionGranularity.Zip))
        .reduce((acc, regions) => [...acc, ...regions], [])
      : null;
  }
  public getAvailableRegionStatusTypes(resultRegion: ResultRegion): RegionSetStatusType[] {
    return this.availableRegionStatuses[keyFor(resultRegion)]
      ? this.availableRegionStatuses[keyFor(resultRegion)].availableStatuses
      : [];
  }
  public getState(regionCode: string): ResultRegion {
    return regionCode && this.serviceArea && this.serviceArea.subRegions
      ? this.serviceArea.subRegions
        .find((sr) => sr.regionGranularity === RegionGranularity.State && sr.regionCode === regionCode)
      : null;
  }
  public getCBSA(regionCode: string): ResultRegion {
    return regionCode && this.serviceArea && this.serviceArea.subRegions
      ? this.serviceArea.subRegions
        .find((sr) => sr.regionGranularity === RegionGranularity.CBSA && sr.regionCode === regionCode)
      : null;
  }
  public getCounty(regionCode: string): ResultRegion {
    const countyMap = regionCode && this.serviceArea && this.serviceArea.subRegions
      ? (this.serviceArea.subRegions.filter((sr) => sr.regionGranularity === RegionGranularity.State || sr.regionGranularity === RegionGranularity.CBSA))
        .map((sr) => sr.subRegions.find((ssr) => ssr.regionGranularity === RegionGranularity.County && ssr.regionCode === regionCode))
      : null;
    return countyMap ? countyMap.find((r) => r !== undefined) : null;
  }
  public getCountyZip(regionCode: string, subRegionCode: string): ResultRegion {
    const countyZipMap = regionCode && this.serviceArea && this.serviceArea.subRegions
      ? this.counties()
        .map((sr) => sr.subRegions.find((ssr) => ssr.regionGranularity === RegionGranularity.CountyZip && ssr.regionCode === regionCode && ssr.subRegionCode === subRegionCode))
      : null;
    return countyZipMap ? countyZipMap.find((r) => r !== undefined) : null;
  }
  public getZip(regionCode: string): ResultRegion {
    const zipMap = regionCode && this.serviceArea && this.serviceArea.subRegions
      ? this.states()
        .map((sr) => sr.subRegions.find((ssr) => ssr.regionGranularity === RegionGranularity.Zip && ssr.regionCode === regionCode))
      : null;
    return zipMap ? zipMap.find((r) => r !== undefined) : null;
  }

  /**
   * Gets the AccuracyAggregate result for the given result-region
   * If a specialtyGroup is not provided, a region-set aggregate type will be returned
   * @param resultRegion
   * @param specialtyGroup
   */
  public async getAccuracyAggregate(
    resultRegion: ResultRegion,
    { specialtyGroup, aggregateDisplayType = "region", inspectionUid, bandFilter, specialtyGroupAggregates }: AggregateOptions = {}
  ): Promise<AccuracyAggregate> {
    if (!resultRegion || !this.accuracyTemplateId) {
      return null;
    }

    if (inspectionUid) {
      return this.getAccuracyInspectionAggregate(resultRegion, { aggregateDisplayType: aggregateDisplayType, bandFilter: bandFilter, inspectionUid: inspectionUid, specialtyGroup: specialtyGroup });
    }

    const key = keyFor(resultRegion, specialtyGroup);
    let accuracyAggregate = this.accuracyAggregates[key];

    if (!accuracyAggregate) {
      if (!specialtyGroup) {
        accuracyAggregate = await this.getAccuracyRegionAggregates([resultRegion], { aggregateDisplayType: aggregateDisplayType, specialtyGroup: specialtyGroup }).then(aggs => {
          return aggs.find(agg => agg.regionSetId === resultRegion.regionSetId);
        });
      } else {
        accuracyAggregate = this.getAccuracySpecialtyGroupAggregate(resultRegion, { aggregateDisplayType: aggregateDisplayType, specialtyGroup: specialtyGroup, specialtyGroupAggregates: specialtyGroupAggregates });
      }
    }

    return accuracyAggregatePseudoClone(accuracyAggregate, aggregateDisplayType);
  }

  /**
   * Gets the AccuracyAggregate result by inspection type
   * @param resultRegion
   * @param specialtyGroup
   */
  private async getAccuracyInspectionAggregate(
    resultRegion: ResultRegion,
    { specialtyGroup, aggregateDisplayType = "region", inspectionUid, bandFilter }: AggregateOptions = {}
  ): Promise<AccuracyAggregate> {
    if (!resultRegion || !this.accuracyTemplateId || !inspectionUid) { // If we're not looking at anything or this run configuration doesn't utilize accuracy...
      return null;
    }

    const specialtyGroupCode = specialtyGroup ? specialtyGroup.code : null;
    let inspectionAggregates: AccuracyAggregateInspectionResultGroup[] = [];
    let inspectionAggregate: AccuracyAggregateInspectionResult = null;

    try {
      inspectionAggregates = (await this.accuracyService
        .accuracyAggregateInspectionResults(this.projectId,
          this.projectRunConfigurationId,
          this.networkAssessmentAnalysisType,
          resultRegion.regionSetId,
          specialtyGroupCode))?.aggregates;
    } catch (e) {
      //handle 'not found' exceptions so page doesn't blow up
      return null;
    }

    const flattened = transformAggregates(inspectionAggregates);

    inspectionAggregate = flattened.find((i) => i.inspectionUid === inspectionUid);

    let providerCount = 0;
    let accuracyIndex = 0;

    if (!inspectionAggregate) {
      return null;
    }

    if(bandFilter){
      const band = inspectionAggregate.bandAggregates.find((b) => (b.name === bandFilter));
      providerCount = band.providerCount;
      accuracyIndex = band.bandPercentage;
    }
    else{
      for (const band of inspectionAggregate.bandAggregates) {
        //we're only counting results if we have 'Inaccurate' or 'Suspect' bands
        if (band.name === "Inaccurate" || band.name === "Suspect") {
          providerCount += band.providerCount;
          accuracyIndex += band.bandPercentage;
        }
      }
    }

    const agg: AccuracySpecialtyGroupAggregate = {
      accuracyIndex,
      bands: inspectionAggregate.bandAggregates,
      providerCount,
      providerFailPercentage: undefined,
      providerPassPercentage: undefined,
      regionSet: undefined,
      resultGroups: undefined,
      specialtyGroupCode,
    };

    if (agg.bands.length === 0) {
      return null;
    }

    const aggKey = keyFor(resultRegion, specialtyGroup);
    return buildAccuracyAggregate(aggKey, resultRegion, agg, aggregateDisplayType, specialtyGroup, undefined);
  }

  /**
   * Gets the AccuracyAggregate result for the given result-region
   * @param resultRegions
   */
  public async getAccuracyRegionAggregates(
    resultRegions: ResultRegion[],
    { aggregateDisplayType = "region" }: AggregateOptions = {},
    useLargeProjectEnhancements?: boolean
  ): Promise<AccuracyAggregate[]> {
    if (!resultRegions || !this.accuracyTemplateId) {
      return null;
    }

    let accuracyAggregates: AccuracyAggregate[] = [];
    let regionResultsPromise: Promise<AccuracyViewByRegionResponse[]>;

    if (!useLargeProjectEnhancements) {
      regionResultsPromise = this.accuracyService.regionResultsBulkBatched(
        this.projectId,
        resultRegions.map(r => r.regionSetId),
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);
    } else {
      regionResultsPromise = this.accuracyService.regionResultsBulk(
        this.projectId,
        resultRegions.map(r => r.regionSetId),
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);
    }

    accuracyAggregates = await regionResultsPromise.then((aggs) => {
      if (!aggs || aggs.length < 0)
        return accuracyAggregates;

      return resultRegions.map<AccuracyAggregate>(rr => {
        const key = keyFor(rr);
        const regionStatus = this.regionStatuses[key];
        const aggregate = aggs.find(agg => agg && agg.regionSetAggregates[rr.canonicalRegionSetId]);
        const regionSetAggregate = aggregate ? aggregate.regionSetAggregates[rr.canonicalRegionSetId] : undefined;
        return buildAccuracyAggregate(key, rr, regionSetAggregate, aggregateDisplayType, undefined, regionStatus);
      });
    });

    // Return copies so some crazy person doesn't manipulate the cached one directly
    return accuracyAggregates.map<AccuracyAggregate>(agg => {
      this.accuracyAggregates[agg.key] = agg;
      return accuracyAggregatePseudoClone(agg, aggregateDisplayType);
    });
  }

  /**
   * Gets the AccuracyAggregate result for the given result-region and specialty group
   * If a specialtyGroup is not provided, a region-set aggregate type will be returned
   * @param resultRegion
   * @param specialtyGroup
   */
  private getAccuracySpecialtyGroupAggregate(
    resultRegion: ResultRegion,
    { specialtyGroup, aggregateDisplayType = "region", specialtyGroupAggregates }: AggregateOptions = {}
  ): AccuracyAggregate {
    if (!resultRegion || !this.accuracyTemplateId || !specialtyGroup) { // If we're not looking at anything or this run configuration doesn't utilize accuracy...
      return null;
    }
    if(!specialtyGroupAggregates)
      return null;

    // build the key to try to find the aggregate
    // try to get the 'cached' aggregate if we don't have it we'll get it
    const key = keyFor(resultRegion, specialtyGroup);
    let accuracyAggregate = this.accuracyAggregates[key];

    if (!accuracyAggregate) {
      const regionStatus = this.regionStatuses[keyFor(resultRegion)];

      // we were given a specialty-group so
      // we'll get the aggregated results for this region broken out by specialty-group
      // build out each result into our UI object
      // while building these out, flag the one we want to be returned
      // before saving it to our 'cache'
      this.specialtyGroups.forEach((sg) => {
        const sgAggregate = specialtyGroupAggregates?.find((sga) => sga.specialtyGroupCode === sg.code);
        const tempKey = keyFor(resultRegion, sg);
        const tempAggregate = buildAccuracyAggregate(tempKey, resultRegion, sgAggregate, aggregateDisplayType, sg, regionStatus);
        if (tempKey === key) {
          accuracyAggregate = tempAggregate;
        }

        this.accuracyAggregates[tempKey] = tempAggregate;
      });
    }

    // Return a copy so some crazy person doesn't manipulate the cached one directly
    return accuracyAggregatePseudoClone(accuracyAggregate, aggregateDisplayType);
  }

  /**
   * Gets the region-set AdequacyAggregate result for the given result-region
   * @param resultRegion The region for which to get the aggregate
   */
  public async getRegionSetAdequacyAggregate(resultRegion: ResultRegion): Promise<AdequacyAggregate> {
    if (!resultRegion) {
      return null;
    }

    const key = keyFor(resultRegion);
    let adequacyAggregate = this.adequacyAggregates[key];

    if (!adequacyAggregate) {
      //try to refill the cache
      await this.getRegionSetAdequacyAggregates([resultRegion]);
      //try getting from the cache again
      adequacyAggregate = this.adequacyAggregates[key];
    }

    return adequacyAggregatePsuedoClone(adequacyAggregate, "region");
  }

  /**
   * Gets the region-set AdequacyAggregate results for the given result-regions
   * @param resultRegion The region for which to get the aggregate
   */
  public async getRegionSetAdequacyAggregates(resultRegions: ResultRegion[]): Promise<AdequacyAggregate[]> {
    if (!resultRegions || !resultRegions.length) {
      return [];
    }

    let regionSetAggregates: AdequacyAggregate[] = [];
    let anyMissingAggregates = false;

    for (const resultRegion of resultRegions) {
      const key = keyFor(resultRegion);

      const adequacyAggregate = this.adequacyAggregates[key];
      if (!adequacyAggregate) {
        anyMissingAggregates = true;
        break;
      } else {
        regionSetAggregates.push(adequacyAggregatePsuedoClone(adequacyAggregate, "region"));
      }
    }

    //some region sets aren't in the cache. Have to re-build the cache from the endpoint results
    if (anyMissingAggregates) {
      regionSetAggregates = [];
      const tempRegionSetAggregates = await this.projectService.regionResults(
        this.projectId,
        resultRegions.map((resultRegion) => resultRegion.regionSetId),
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      resultRegions.forEach((resultRegion) => {
        const rsAggregate = tempRegionSetAggregates.find((rsa) => rsa.regionSetId === resultRegion.canonicalRegionSetId);

        //Note that the aggregate is added regardless of if we actually have one.
        //Filtering out the null aggregates breaks when only opportunity aggregates exist (or vice versa if filtered to opportunity).
        const tempKey = keyFor(resultRegion);
        const regionStatus = this.regionStatuses[tempKey];
        const tempAggregate = buildAdequacyAggregate(tempKey, resultRegion, rsAggregate, "region", undefined, regionStatus, Object.values(this.adequacyAggregates).some((aa) => aa.specialtyGroupIsExpandable));
        this.adequacyAggregates[tempKey] = tempAggregate;

        regionSetAggregates.push(tempAggregate);
      });
    }

    return regionSetAggregates;
  }

  /**
   * Gets the region-set-specialty-group AdequacyAggregate result for the given result-region and specialty group
   * @param resultRegion The region for which to get the aggregate
   */
  public async getRegionSetSpecialtyGroupAdequacyAggregate(resultRegion: ResultRegion, specialtyGroup: AnalysisSpecialtyGroup): Promise<AdequacyAggregate> {
    if (!resultRegion || !specialtyGroup || !this.adequacyTemplateId) {
      return null;
    }

    const key = keyFor(resultRegion, specialtyGroup);
    let adequacyAggregate = this.adequacyAggregates[key];

    if (!adequacyAggregate) {
      //try to refill the cache
      await this.getRegionSetSpecialtyGroupAdequacyAggregates(resultRegion);
      //try getting from the cache again
      adequacyAggregate = this.adequacyAggregates[key];
    }

    return adequacyAggregatePsuedoClone(adequacyAggregate, "region");
  }

  /**
   * Gets the region-set-specialty-group AdequacyAggregate results for the given result-region
   * @param resultRegion The region for which to get the aggregate
   */
  public async getRegionSetSpecialtyGroupAdequacyAggregates(resultRegion: ResultRegion): Promise<AdequacyAggregate[]> {
    if (!resultRegion || !this.adequacyTemplateId) {
      return [];
    }

    let specialtyGroupAggregates: AdequacyAggregate[] = [];
    let anyMissingAggregates = false;

    for (const specialtyGroup of this.specialtyGroups) {
      const key = keyFor(resultRegion, specialtyGroup);

      const adequacyAggregate = this.adequacyAggregates[key];
      if (!adequacyAggregate) {
        anyMissingAggregates = true;
        break;
      } else {
        specialtyGroupAggregates.push(adequacyAggregatePsuedoClone(adequacyAggregate, "specialty"));
      }
    }

    //some region-set specialty groups aren't in the cache. Have to re-build the cache from the endpoint results
    if (anyMissingAggregates) {
      //clear the aggregates and refill with the new results
      specialtyGroupAggregates = [];

      const tempSpecialtyGroupAggregates = await this.projectService.specialtyGroupResults(
        this.projectId,
        resultRegion.regionSetId,
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      const regionStatus = this.regionStatuses[keyFor(resultRegion)];

      this.specialtyGroups.forEach((sg) => {
        const sgAggregate = tempSpecialtyGroupAggregates.find((sga) => sga.specialtyGroupCode === sg.code);

        //Note that the aggregate is added regardless of if we actually have one.
        //Filtering out the null aggregates breaks when only opportunity aggregates exist (or vice versa if filtered to opportunity).
        const tempKey = keyFor(resultRegion, sg);
        const tempAggregate = buildAdequacyAggregate(tempKey, resultRegion, sgAggregate, "specialty", sg, regionStatus);
        specialtyGroupAggregates.push(tempAggregate);
        this.adequacyAggregates[tempKey] = tempAggregate;
      });
    }

    return specialtyGroupAggregates;
  }

  /**
   * Gets the region-set-specialty-group AdequacyAggregate result for the given result-region and its children
   * @param resultRegion The region for which to get the aggregate
   */
  public async getBulkRegionSetSpecialtyGroupAdequacyAggregates(resultRegions: ResultRegion[], specialtyGroup: AnalysisSpecialtyGroup): Promise<AdequacyAggregate[]> {
    if (!resultRegions || !resultRegions.length || !specialtyGroup) {
      return null;
    }

    let specialtyGroupAggregates: AdequacyAggregate[] = [];
    let anyMissingAggregates = false;

    for (const resultRegion of resultRegions) {
      const key = keyFor(resultRegion, specialtyGroup);

      const adequacyAggregate = this.adequacyAggregates[key];
      if (!adequacyAggregate) {
        anyMissingAggregates = true;
        break;
      } else {
        specialtyGroupAggregates.push(adequacyAggregatePsuedoClone(adequacyAggregate, "region"));
      }
    }

    //some region-set specialty groups aren't in the cache. Have to re-build the cache from the endpoint results
    if (anyMissingAggregates) {
      //clear the aggregates and refill with the new results
      specialtyGroupAggregates = [];

      const adequacyResultSpecialtyGroupsByRegion = await this.projectService.specialtyGroupResultsBulk(
        this.projectId,
        specialtyGroup.code,
        resultRegions,
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      if (adequacyResultSpecialtyGroupsByRegion.length > 0) {
        resultRegions.forEach((resultRegion) => {
          const tempSpecialtyGroupAggregates = adequacyResultSpecialtyGroupsByRegion.find((sga) => sga.regionSetId === resultRegion.canonicalRegionSetId).aggregates;
          if (tempSpecialtyGroupAggregates && tempSpecialtyGroupAggregates.length) {
            const sgAggregate = tempSpecialtyGroupAggregates.find((sga) => sga.specialtyGroupCode === specialtyGroup.code);
            if (sgAggregate) {
              //build the cache
              const tempKey = keyFor(resultRegion, specialtyGroup);
              const regionStatus = this.regionStatuses[keyFor(resultRegion)];
              const tempAggregate = buildAdequacyAggregate(tempKey, resultRegion, sgAggregate, "region", specialtyGroup, regionStatus);
              this.adequacyAggregates[tempKey] = tempAggregate;

              specialtyGroupAggregates.push(tempAggregate);
            }
          }
        });
      }
    }

    return specialtyGroupAggregates;
  }

  /**
   * Gets the region-set-specialty-group AccuracyAggregate result for the given result-region and its children
   * @param resultRegion The region for which to get the aggregate
   */
  public async getBulkRegionSetSpecialtyGroupAccuracyAggregates(resultRegions: ResultRegion[], specialtyGroup: AnalysisSpecialtyGroup): Promise<AccuracyAggregate[]> {
    if (!resultRegions || !resultRegions.length || !specialtyGroup || !this.accuracyTemplateId) { // If we're not looking at anything or this run configuration doesn't utilize accuracy...
      return null;
    }

    let specialtyGroupAggregates: AccuracyAggregate[] = [];
    let anyMissingAggregates = false;

    // build the key to try to find the aggregate
    // try to get the 'cached' aggregate if we don't have it we'll get it
    for (const resultRegion of resultRegions) {
      const key = keyFor(resultRegion, specialtyGroup);
      const accuracyAggregate = this.accuracyAggregates[key];

      if (!accuracyAggregate) {
        anyMissingAggregates = true;
        break;
      } else {
        specialtyGroupAggregates.push(accuracyAggregatePseudoClone(accuracyAggregate, "region"));
      }
    }

    if (anyMissingAggregates) {
      // clear the aggregates and refill with the new results
      specialtyGroupAggregates = [];

      const accuracyResultSpecialtyGroupsByRegion = await this.accuracyService.specialtyGroupResultsBulk(
        this.projectId,
        specialtyGroup.code,
        resultRegions,
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      accuracyResultSpecialtyGroupsByRegion.forEach((accuracyAggregateResult) => {

        const resultRegion = resultRegions.find((r) => r.canonicalRegionSetId === accuracyAggregateResult.regionSetId);
        const accuracyAggregate = accuracyAggregateResult.aggregates && accuracyAggregateResult.aggregates.find((a) => a.specialtyGroupCode === specialtyGroup.code);

        if (resultRegion && accuracyAggregate) {
          // build the cache
          const tempKey = keyFor(resultRegion, specialtyGroup);
          const regionStatus = this.regionStatuses[keyFor(resultRegion)];
          const tempAggregate = buildAccuracyAggregate(tempKey, resultRegion, accuracyAggregate, "region", specialtyGroup, regionStatus);
          this.accuracyAggregates[tempKey] = tempAggregate;

          // add a copy so some crazy person doesn't manipulate the cached one directly
          specialtyGroupAggregates.push(accuracyAggregatePseudoClone(tempAggregate, "region"));
        }
      });
    }

    return specialtyGroupAggregates;
  }

  /**
   * Gets the AccuracyDetail results for the given result-region
   * -When specialty-group provided: The details for all specialty-groups are returned for that region-set
   * -When specialty-group not provided & result-region does not have sub-regions: The details for that specialty-group/region-set is returned (single item in array)
   * -When specialty-group not provided & result-region has sub-regions: The details for that specialty-group is returned for each sub-region
   * @param resultRegion
   * @param specialtyGroup
   * @param aggregateDisplayType
   */
  public async getAccuracyDetails(
    resultRegion: ResultRegion,
    specialtyGroup?: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType = "region",
  ): Promise<AccuracyDetail[]> {
    if (!resultRegion || !this.accuracyTemplateId) {
      return null;
    }

    if (!specialtyGroup) {
      const regionKey = keyFor(resultRegion);
      let accuracyDetails = this.accuracyDetails[regionKey];

      if (!accuracyDetails || Object.keys(accuracyDetails).length !== this.specialtyGroups.length) {
        accuracyDetails = {};

        const regionSetDetails = await this.accuracyService.regionDetails(
          this.projectId,
          resultRegion.regionSetId,
          this.projectRunConfigurationId,
          this.networkAssessmentAnalysisType,
        );

        const accuracySummarySpecialtyGroups = (regionSetDetails && regionSetDetails[0] || undefined)
          && (regionSetDetails[0].specialtyGroupSummaries)
          || undefined;

        if (!accuracySummarySpecialtyGroups) {
          return undefined;
        }

        accuracySummarySpecialtyGroups.forEach((sgs) => {
          const tempSpecialtyGroup = this.specialtyGroups.find((sg) => sg.code === sgs.specialtyGroupCode);
          const tempRegionGroupKey = keyFor(resultRegion, tempSpecialtyGroup);
          accuracyDetails[tempRegionGroupKey] = buildAccuracyDetail(
            tempRegionGroupKey,
            resultRegion,
            sgs,
            tempSpecialtyGroup,
            this.regionSpecialtyGroupExceptions[tempRegionGroupKey],
            aggregateDisplayType,
          );
        });

        this.accuracyDetails[regionKey] = accuracyDetails;
      }

      return Object.keys(accuracyDetails).map((k) => accuracyDetailPseudoClone(accuracyDetails[k], aggregateDisplayType));
    } else {
      if (!resultRegion.subRegions || resultRegion.subRegions.length === 0) {
        const regionKey = keyFor(resultRegion);
        const regionGroupKey = keyFor(resultRegion, specialtyGroup);

        let accuracyDetails = this.accuracyDetails[regionKey];
        let accuracyDetailsGroup = accuracyDetails
          ? accuracyDetails[regionGroupKey]
          : undefined;

        if (!accuracyDetails || !accuracyDetailsGroup) {
          accuracyDetails = accuracyDetails
            ? accuracyDetails
            : {};

          const regionSetDetails = await this.accuracyService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
            this.networkAssessmentAnalysisType,
          );

          const accuracySummarySpecialtyGroup = (regionSetDetails && regionSetDetails[0] || undefined)
            && (regionSetDetails[0].specialtyGroupSummaries && regionSetDetails[0].specialtyGroupSummaries[0] || undefined)
            || undefined;

          if (!accuracySummarySpecialtyGroup) {
            return undefined;
          }

          accuracyDetailsGroup = buildAccuracyDetail(
            regionGroupKey,
            resultRegion,
            accuracySummarySpecialtyGroup,
            specialtyGroup,
            this.regionSpecialtyGroupExceptions[regionGroupKey],
            aggregateDisplayType,
          );

          accuracyDetails[regionGroupKey] = accuracyDetailsGroup;

          this.accuracyDetails[regionKey] = accuracyDetails;
        }

        return [accuracyDetailPseudoClone(accuracyDetailsGroup, aggregateDisplayType)];
      } else {
        let results: AccuracyDetail[] = [];

        for (const sr of resultRegion.subRegions) {
          const subregionKey = keyFor(sr);
          const subregionGroupKey = keyFor(sr, specialtyGroup);

          let accuracyDetails = this.accuracyDetails[subregionKey];
          let accuracyDetailsGroup = accuracyDetails
            ? accuracyDetails[subregionGroupKey]
            : undefined;

          if (!accuracyDetails || !accuracyDetailsGroup) {
            accuracyDetails = accuracyDetails
              ? accuracyDetails
              : {};

            const regionSetDetails = await this.accuracyService.regionDetailsByRegionSet(
              this.projectId,
              sr.canonicalRegionSetId,
              this.projectRunConfigurationId,
              specialtyGroup.code,
              this.networkAssessmentAnalysisType,
            );

            const accuracySummarySpecialtyGroup = (regionSetDetails && regionSetDetails[0] || undefined)
              && (regionSetDetails[0].specialtyGroupSummaries && regionSetDetails[0].specialtyGroupSummaries[0] || undefined)
              || undefined;

            accuracyDetailsGroup = buildAccuracyDetail(
              subregionGroupKey,
              resultRegion,
              accuracySummarySpecialtyGroup,
              specialtyGroup,
              this.regionSpecialtyGroupExceptions[subregionGroupKey],
              aggregateDisplayType,
            );

            accuracyDetails[subregionGroupKey] = accuracyDetailsGroup;

            this.accuracyDetails[subregionKey] = accuracyDetails;
          }

          if (accuracyDetailsGroup) {
            results.push(accuracyDetailPseudoClone(accuracyDetailsGroup, aggregateDisplayType));
          }
        }

        if (results.length === resultRegion.subRegions.length) {
          results = [];
          const regionSetDetails = await this.accuracyService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
            this.networkAssessmentAnalysisType,
          );

          regionSetDetails.forEach((rsd) => {
            const accuracySummarySpecialtyGroup = rsd.specialtyGroupSummaries && rsd.specialtyGroupSummaries[0] || undefined;
            if (accuracySummarySpecialtyGroup) {
              const subRegionResult = resultRegion.subRegions.find((sr) => sr.canonicalRegionSetId === rsd.regionSet.regionSetId);
              const regionKey = keyFor(subRegionResult);
              const regionGroupKey = keyFor(subRegionResult, specialtyGroup);
              const details = buildAccuracyDetail(
                regionGroupKey,
                subRegionResult,
                rsd.specialtyGroupSummaries[0],
                specialtyGroup,
                this.regionSpecialtyGroupExceptions[regionGroupKey],
                aggregateDisplayType,
              );

              let accuracyDetails = this.accuracyDetails[regionKey];
              accuracyDetails = accuracyDetails
                ? accuracyDetails
                : {};
              accuracyDetails[regionGroupKey] = details;

              this.accuracyDetails[regionKey] = accuracyDetails;

              results.push(accuracyDetailPseudoClone(details, aggregateDisplayType));
            }
          });
        }

        return results;
      }
    }
  }

  public async getBatchedAccuracyDetails(resultRegions: ResultRegion[], skip?: number, take?: number, sortDirection?: SortDirection, selectedColumn?: string, sortColor?: string): Promise<AccuracyDetailsBatch> {
    const aggregateDisplayType = "region";
    const batchedAccuracyDetails: AccuracyDetail[] = [];
    let totalAggregateCount = 0;
    let totalProviderCount = 0;
    let sortingColorMap: Dictionary<string[]>;

    if (resultRegions.length) {
      const accuracyRegionSetSummaryBulk = await this.accuracyService.viewByAll(
        this.projectId,
        resultRegions.map((rr) => rr.regionSetId),
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType,
        skip,
        take,
        sortDirection,
        selectedColumn,
        sortColor,
      );

      totalAggregateCount = accuracyRegionSetSummaryBulk.totalAggregateCount;
      totalProviderCount = accuracyRegionSetSummaryBulk.totalProviderCount;
      sortingColorMap = accuracyRegionSetSummaryBulk.sortingColorMap;

      if (accuracyRegionSetSummaryBulk && accuracyRegionSetSummaryBulk.accuracySummaries) {
        accuracyRegionSetSummaryBulk.accuracySummaries.forEach((rsd) => {
          const tempSpecialtyGroup = this.specialtyGroups.find((sg) => sg.code === rsd.specialtyGroupSummary.specialtyGroupCode);
          const resultRegion = resultRegions.find((rr) => rr.canonicalRegionSetId === rsd.regionSet.regionSetId);
          const regionGroupKey = keyFor(resultRegion, tempSpecialtyGroup);
          const details = buildAccuracyDetail(
            regionGroupKey,
            resultRegion,
            rsd.specialtyGroupSummary,
            tempSpecialtyGroup,
            this.regionSpecialtyGroupExceptions[regionGroupKey],
            aggregateDisplayType);

          batchedAccuracyDetails.push(accuracyDetailPseudoClone(details, aggregateDisplayType));
        });
      }
    }

    return { accuracyDetails: batchedAccuracyDetails, sortingColorMap, totalAggregateCount, totalProviderCount };
  }

  /**
   * Gets the CompositeAggregate result for the given result-region
   * If a specialtyGroup is not provided, a region-set aggregate type will be returned
   * @param resultRegion The region for which to get the aggregate
   * @param aggregateDisplayType The type to be used when building the aggregate
   * @param specialtyGroup [Optional] The specialty group for which to get the aggregate
   */
  public async getCompositeAggregate(
    resultRegion: ResultRegion,
    specialtyGroup?: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType = "region",
  ): Promise<CompositeAggregate> {

    if (!resultRegion) {
      return null;
    }

    // build the key to try to find the aggregate
    // try to get the 'cached' aggregate if we don't have it we'll get it
    const key = keyFor(resultRegion, specialtyGroup);

    const accuracyAggregate = await this.getAccuracyAggregate(resultRegion, { aggregateDisplayType: aggregateDisplayType, specialtyGroup: specialtyGroup });
    const adequacyAggregate = await this.getRegionSetAdequacyAggregate(resultRegion);
    const attestationAggregate: AttestationAggregate = null;

    let compositeAggregate; // = { ...accuracyAggregate, ...adequacyAggregate, ...attestationAggregate };

    const regionStatus = this.regionStatuses[keyFor(resultRegion)];
    if (!specialtyGroup) {
      // we weren't given a specialty-group so
      // we'll get aggregated results for this region
      const regionSetAggregate: CompositeRegionSetAggregate = { ...accuracyAggregate, ...adequacyAggregate, ...attestationAggregate };

      compositeAggregate = buildCompositeAggregate(key, resultRegion, regionSetAggregate, aggregateDisplayType, undefined, regionStatus);
    } else {
      // we were given a specialty-group so
      // we'll get the aggregated results for this region broken out by specialty-group
      const specialtyGroupAggregate: CompositeSpecialtyGroupAggregate = { ...accuracyAggregate, ...adequacyAggregate, ...attestationAggregate };

      compositeAggregate = buildCompositeAggregate(key, resultRegion, specialtyGroupAggregate, aggregateDisplayType, specialtyGroup, regionStatus);
    }

    //Return a copy so some crazy person doesn't manipulate the cached one directly
    return compositeAggregatePseudoClone(compositeAggregate, aggregateDisplayType);
  }

  /**
   * Gets the AdequacyDetail results for the given result-region and optional specialty-group
   * -When specialty-group provided: The details for all specialty-groups are returned for that region-set
   * -When specialty-group not provided & result-region does not have sub-regions: The details for that specialty-group/region-set is returned (single item in array)
   * -When specialty-group not provided & result-region has sub-regions: The details for that specialty-group is returned for each sub-region
   * @param resultRegion
   * @param specialtyGroup
   */
  public async getAdequacyDetails(
    resultRegion: ResultRegion,
    specialtyGroup?: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType = "region",
  ): Promise<AdequacyDetail[]> {
    if (!resultRegion) {
      return null;
    }

    if (!specialtyGroup) {
      return await this.getAdequacyDetailsInternal(resultRegion, aggregateDisplayType);
    } else {
      return this.getAdequacyDetailsBySpecialtyInternal(resultRegion, specialtyGroup, aggregateDisplayType);
    }
  }

  /**
   * Gets the CompositeDetail results for the given result-region and optional specialty-group
   * -When specialty-group provided: The details for all specialty-groups are returned for that region-set
   * -When specialty-group not provided & result-region does not have sub-regions: The details for that specialty-group/region-set is returned (single item in array)
   * -When specialty-group not provided & result-region has sub-regions: The details for that specialty-group is returned for each sub-region
   * @param resultRegion
   * @param specialtyGroup
   */
  public async getCompositeDetails(
    resultRegion: ResultRegion,
    specialtyGroup?: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType = "region",
  ): Promise<CompositeDetail[]> {
    if (!resultRegion) {
      return null;
    }

    if (!specialtyGroup) {
      const regionKey = keyFor(resultRegion);
      let compositeDetails = this.compositeDetails[regionKey];

      if (!compositeDetails || Object.keys(compositeDetails).length !== this.specialtyGroups.length) {

        compositeDetails = {};
        const adequacyRegionSetDetails = this.adequacyTemplateId ? await this.projectService.regionDetails(
          this.projectId,
          resultRegion.regionSetId,
          this.projectRunConfigurationId,
          this.networkAssessmentAnalysisType) : [];

        const attestationRegionSetDetails = await this.attestationService.regionDetails(
          this.projectId,
          resultRegion.regionSetId,
          this.projectRunConfigurationId,
        );

        const accuracyRegionSetDetails = this.accuracyTemplateId ? await this.accuracyService.regionDetails(
          this.projectId,
          resultRegion.regionSetId,
          this.projectRunConfigurationId,
          this.networkAssessmentAnalysisType,
        ) : null;

        const adequacySummarySpecialtyGroups = (adequacyRegionSetDetails && adequacyRegionSetDetails[0] || undefined)
          && (adequacyRegionSetDetails[0].specialtyGroupSummaries)
          || undefined;

        const attestationSummarySpecialtyGroups = (attestationRegionSetDetails && attestationRegionSetDetails[0] || undefined)
          && (attestationRegionSetDetails[0].specialtyGroupSummaries)
          || undefined;

        const accuracySummarySpecialtyGroups = (accuracyRegionSetDetails && accuracyRegionSetDetails[0] || undefined)
          && (accuracyRegionSetDetails[0].specialtyGroupSummaries)
          || undefined;

        const compositeSummarySpecialtyGroups = [];
        if (accuracySummarySpecialtyGroups) {
          compositeSummarySpecialtyGroups.push({ ...adequacySummarySpecialtyGroups[0], ...attestationSummarySpecialtyGroups[0], ...accuracySummarySpecialtyGroups[0] });
        } else {
          compositeSummarySpecialtyGroups.push({ ...adequacySummarySpecialtyGroups[0], ...attestationSummarySpecialtyGroups[0] });
        }

        compositeSummarySpecialtyGroups.forEach((sgs) => {
          const tempSpecialtyGroup = this.specialtyGroups.find((sg) => sg.code === sgs.specialtyGroupCode) || this.specialtyGroups.find((sg) => sg.code === sgs.specialtyGroup.code);
          const tempRegionGroupKey = keyFor(resultRegion, tempSpecialtyGroup);
          compositeDetails[tempRegionGroupKey] = buildCompositeDetail(
            tempRegionGroupKey,
            resultRegion,
            sgs,
            tempSpecialtyGroup,
            this.regionSpecialtyGroupExceptions[tempRegionGroupKey],
            aggregateDisplayType,
          );
        });

        this.compositeDetails[regionKey] = compositeDetails;
      }

      return Object.keys(compositeDetails).map((k) => compositeDetailPseudoClone(compositeDetails[k], aggregateDisplayType));
    } else {

      if (!resultRegion.subRegions || resultRegion.subRegions.length === 0) {

        const regionKey = keyFor(resultRegion);
        const regionGroupKey = keyFor(resultRegion, specialtyGroup);
        let compositeDetails = this.compositeDetails[regionKey];
        let compositeDetailsGroup = compositeDetails
          ? compositeDetails[regionGroupKey]
          : undefined;

        if (!compositeDetails || !compositeDetailsGroup) {

          compositeDetails = compositeDetails
            ? compositeDetails
            : {};

          const adequacyRegionSetDetails = await this.projectService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
            this.networkAssessmentAnalysisType);

          const adequacySummarySpecialtyGroup = (adequacyRegionSetDetails && adequacyRegionSetDetails[0] || undefined)
            && (adequacyRegionSetDetails[0].specialtyGroupSummaries && adequacyRegionSetDetails[0].specialtyGroupSummaries[0] || undefined)
            || undefined;

          const attestationRegionSetDetails = await this.attestationService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
          );

          const attestationSummarySpecialtyGroup = (attestationRegionSetDetails && attestationRegionSetDetails[0] || undefined)
            && (attestationRegionSetDetails[0].specialtyGroupSummaries && attestationRegionSetDetails[0].specialtyGroupSummaries[0] || undefined)
            || undefined;

          const accuracyRegionSetDetails = this.accuracyTemplateId && await this.accuracyService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
            this.networkAssessmentAnalysisType,
          ) || null;

          const accuracySummarySpecialtyGroup = (accuracyRegionSetDetails && accuracyRegionSetDetails[0] || undefined)
            && (accuracyRegionSetDetails[0].specialtyGroupSummaries && accuracyRegionSetDetails[0].specialtyGroupSummaries[0] || undefined)
            || undefined;

          const compositeSummarySpecialtyGroup: CompositeSummarySpecialtyGroup = { ...adequacySummarySpecialtyGroup, ...attestationSummarySpecialtyGroup, ...accuracySummarySpecialtyGroup };

          compositeDetailsGroup = buildCompositeDetail(
            regionGroupKey,
            resultRegion,
            compositeSummarySpecialtyGroup,
            specialtyGroup,
            this.regionSpecialtyGroupExceptions[regionGroupKey],
            aggregateDisplayType);

          compositeDetails[regionGroupKey] = compositeDetailsGroup;

          this.compositeDetails[regionKey] = compositeDetails;
        }

        return [compositeDetailPseudoClone(compositeDetailsGroup, aggregateDisplayType)];
      } else {
        let results: CompositeDetail[] = [];

        resultRegion.subRegions.forEach((sr) => {
          const compositeDetails = this.compositeDetails[keyFor(sr)];
          const compositeDetailsGroup = compositeDetails
            ? compositeDetails[keyFor(sr, specialtyGroup)]
            : undefined;
          if (compositeDetailsGroup) {
            results.push(compositeDetailPseudoClone(compositeDetailsGroup, aggregateDisplayType));
          }
        });

        if (results.length !== resultRegion.subRegions.length) {
          results = [];

          const adequacyRegionSetDetails = await this.projectService.regionDetails(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            this.networkAssessmentAnalysisType);
          const rsdAdequacy = adequacyRegionSetDetails && adequacyRegionSetDetails[0] ? adequacyRegionSetDetails[0] : undefined;

          const attestationRegionSetDetails = await this.attestationService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
          );
          const rsdAttestation = attestationRegionSetDetails && attestationRegionSetDetails[0] ? attestationRegionSetDetails[0] : undefined;

          const accuracyRegionSetDetails = this.accuracyTemplateId && await this.accuracyService.regionDetailsByRegionSet(
            this.projectId,
            resultRegion.regionSetId,
            this.projectRunConfigurationId,
            specialtyGroup.code,
            this.networkAssessmentAnalysisType,
          ) || null;
          const rsdAccuracy = accuracyRegionSetDetails && accuracyRegionSetDetails[0] ? accuracyRegionSetDetails[0] : undefined;

          const compositeRegionSetDetails = [];
          compositeRegionSetDetails.push({ ...rsdAccuracy, ...rsdAdequacy, ...rsdAttestation });

          compositeRegionSetDetails.forEach((rsd) => {
            const compositeSummarySpecialtyGroup: CompositeSummarySpecialtyGroup = rsd.specialtyGroupSummaries && rsd.specialtyGroupSummaries[0] || undefined;
            if (compositeSummarySpecialtyGroup) {
              const subRegionResult = resultRegion.subRegions.find((sr) => sr.regionSetId === rsd.regionSet.regionSetId);
              const regionKey = keyFor(subRegionResult);
              const regionGroupKey = keyFor(subRegionResult, specialtyGroup);
              const details = buildCompositeDetail(
                regionGroupKey,
                subRegionResult,
                rsd.specialtyGroupSummaries[0],
                specialtyGroup,
                this.regionSpecialtyGroupExceptions[regionGroupKey],
                aggregateDisplayType);

              let compositeDetails = this.compositeDetails[regionKey];
              compositeDetails = compositeDetails
                ? compositeDetails
                : {};
              compositeDetails[regionGroupKey] = details;

              this.compositeDetails[regionKey] = compositeDetails;

              results.push(compositeDetailPseudoClone(details, aggregateDisplayType));
            }
          });
        }

        return results;
      }
    }
  }

  public async getAdequacyTrendChartData(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, specialtyGroup?: AnalysisSpecialtyGroup): Promise<AdequacyTrendChartItem[]> {
    const key = keyFor(regionSetQualifier, specialtyGroup);
    let adequacyTrendChartItems = this.adequacyTrendChartData[key] || null;

    if (!regionSetQualifier) {
      return [];
    }

    const dualMembershipRunConfigurationType = this.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental ? ProjectRunConfigurationType.DualMembershipSupplemental : ProjectRunConfigurationType.DualMembershipPrimary;

    if (!adequacyTrendChartItems || adequacyTrendChartItems.length === 0
      || adequacyTrendChartItems[0].runFrequency !== runFrequency) {
      if (!specialtyGroup || !specialtyGroup.code) {
        if (regionSetQualifier.regionSetId === this.serviceAreaRegionSetId) { // at the top-level region set, get the project-level trend charts
          adequacyTrendChartItems = await this.projectService.getTrendChartData(this.projectId, this.networkAssessmentAnalysisType, runFrequency, dualMembershipRunConfigurationType);
        } else {
          adequacyTrendChartItems = await this.projectService.getTrendChartDataByRegionSet(this.projectId, regionSetQualifier.regionSetId, this.networkAssessmentAnalysisType, runFrequency, dualMembershipRunConfigurationType);
        }
      } else {
        adequacyTrendChartItems = await this.projectService.getTrendChartDataRegionSetSpecialtyGroup(this.projectId, regionSetQualifier.regionSetId, specialtyGroup.code, this.networkAssessmentAnalysisType, runFrequency, dualMembershipRunConfigurationType);
      }

      this.adequacyTrendChartData[key] = adequacyTrendChartItems;
    }

    return adequacyTrendChartItems || [];
  }

  /**
   * Get trend chart data for rendering
   * @param regionSetQualifier
   * @param specialtyGroup
   */
  public async getAccuracyTrendChartData(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, specialtyGroup?: AnalysisSpecialtyGroup, inspection?: string): Promise<AccuracyTrendChartItem[]> {
    const key = keyFor(regionSetQualifier, specialtyGroup, inspection);
    let accuracyTrendChartItems = this.accuracyTrendChartData[key] || null;

    if (!accuracyTrendChartItems || (accuracyTrendChartItems[0] && accuracyTrendChartItems[0].runFrequency) !== runFrequency) {
      accuracyTrendChartItems = await this.accuracyService.getTrendChartData(
        this.projectId,
        this.networkAssessmentAnalysisType,
        runFrequency,
        //regionSetQualifier.regionGranularity will only be "unknown" at service area level.
        regionSetQualifier && regionSetQualifier.regionGranularity !== RegionGranularity.Unknown ? regionSetQualifier.regionSetId : null,
        specialtyGroup && specialtyGroup.code || undefined,
        inspection,
      );

      this.accuracyTrendChartData[key] = accuracyTrendChartItems;
    }

    if (!regionSetQualifier) {
      return [];
    }

    return accuracyTrendChartItems;
  }

  public async getTrendChartDataDualMembership(runFrequency: TimeFrequency, regionSetQualifier: RegionSetQualifier, runConfigurationType: ProjectRunConfigurationType, specialtyGroup?: AnalysisSpecialtyGroup): Promise<AdequacyTrendChartItem[]> {
    let adequacyTrendChartItems: AdequacyTrendChartItem[];

    if (regionSetQualifier) {
      if (!specialtyGroup || !specialtyGroup.code) {
        if (regionSetQualifier.regionSetId === this.serviceAreaRegionSetId) { // at the top-level region set, get the project-level trend charts
          adequacyTrendChartItems = await this.projectService.getTrendChartData(this.projectId, this.networkAssessmentAnalysisType, runFrequency, runConfigurationType);
        } else {
          adequacyTrendChartItems = await this.projectService.getTrendChartDataByRegionSet(this.projectId, regionSetQualifier.regionSetId, this.networkAssessmentAnalysisType, runFrequency, runConfigurationType);
        }
      } else {
        adequacyTrendChartItems = await this.projectService.getTrendChartDataRegionSetSpecialtyGroup(this.projectId, regionSetQualifier.regionSetId, specialtyGroup.code, this.networkAssessmentAnalysisType, runFrequency, runConfigurationType);
      }
    }

    return adequacyTrendChartItems;
  }

  /**
   * Updates the region status and returns the updated AdquacyAggregate
   * @param region
   * @param newStatus
   */
  public async setStatus(region: RegionSetQualifier, newStatus: RegionSetStatusType): Promise<[AccuracyAggregate, AdequacyAggregate, AttestationAggregate]> {
    if (!newStatus) {
      await this.projectService.deleteStatus(this.projectId, region.regionSetId, this.projectRunConfigurationId);
    } else {
      await this.projectService.setStatus(this.projectId, region.regionSetId, newStatus);
    }

    const regionKey = keyFor(region);
    this.regionStatuses = { ...this.regionStatuses, [regionKey]: newStatus };

    const accuracyAggregate = this.accuracyAggregates[regionKey];
    const adequacyAggregate = this.adequacyAggregates[regionKey];
    const attestationAggregate = this.attestationAggregates[regionKey];

    adequacyAggregate.regionStatus = newStatus;

    return [accuracyAggregate, adequacyAggregate, attestationAggregate];
  }

  /**
   * Updates the specialty group exception
   * @param exception
   */
  public async setException(exception: SpecialtyGroupException): Promise<[string, SpecialtyGroupException]> {
    const regionGroupKey = keyFor(exception, exception.specialtyGroupCode);
    if (this.regionSpecialtyGroupExceptions[regionGroupKey]) {
      const specialtyGroupExceptionUpdated = await this.specialtyGroupExceptionsService.updateSpecialtyGroupException(this.projectId, exception);
      return [regionGroupKey, specialtyGroupExceptionUpdated];
    } else {
      const specialtyGroupExceptionsCreated = await this.specialtyGroupExceptionsService.createSpecialtyGroupException(this.projectId, exception);
      return [regionGroupKey, specialtyGroupExceptionsCreated];
    }
  }

  private async getAdequacyDetailsInternal(
    resultRegion: ResultRegion,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    if (resultRegion.regionGranularity === RegionGranularity.County || !resultRegion.subRegions || resultRegion.subRegions.length === 0) {

      return await this.getCountyAdequacyDetailsInternal(resultRegion, aggregateDisplayType);
    } else {

      return this.getParentRegionAdequacyDetailsInternal(resultRegion, aggregateDisplayType);
    }
  }

  private async getCountyAdequacyDetailsInternal(
    resultRegion: ResultRegion,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    const regionKey = keyFor(resultRegion);
    let adequacyDetails = this.adequacyDetails[regionKey];

    if (!adequacyDetails || Object.keys(adequacyDetails).length !== this.specialtyGroups.length) {

      adequacyDetails = {};
      const regionSetDetails = await this.projectService.regionDetails(
        this.projectId,
        resultRegion.regionSetId,
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      const adequacySummarySpecialtyGroups = (regionSetDetails && regionSetDetails[0] || undefined)
        && (regionSetDetails[0].specialtyGroupSummaries)
        || undefined;

      if (!adequacySummarySpecialtyGroups) {
        return undefined;
      }

      adequacySummarySpecialtyGroups.forEach((sgs) => {
        const tempSpecialtyGroup = this.specialtyGroups.find((sg) => sg.code === sgs.specialtyGroup.code);
        const tempRegionGroupKey = keyFor(resultRegion, tempSpecialtyGroup);
        adequacyDetails[tempRegionGroupKey] = buildAdequacyDetail(
          tempRegionGroupKey,
          resultRegion,
          sgs,
          tempSpecialtyGroup,
          this.regionSpecialtyGroupExceptions[tempRegionGroupKey],
          aggregateDisplayType);
      });

      this.adequacyDetails[regionKey] = adequacyDetails;
    }

    return Object.keys(adequacyDetails).map((k) => adequacyDetailPsuedoClone(adequacyDetails[k], aggregateDisplayType));
  }

  private async getParentRegionAdequacyDetailsInternal(
    resultRegion: ResultRegion,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    let results: AdequacyDetail[] = [];
    const regionKey = keyFor(resultRegion);
    const granularSubRegions = this.getGranularSubRegions(resultRegion, RegionGranularity.County);

    granularSubRegions.forEach((sr) => {
      const adequacyDetails = this.adequacyDetails[keyFor(sr)];

      const adequacyDetailsGroup = adequacyDetails
        ? adequacyDetails[regionKey]
        : undefined;
      if (adequacyDetailsGroup) {
        results.push(adequacyDetailPsuedoClone(adequacyDetailsGroup, aggregateDisplayType));
      }
    });

    if (results.length !== granularSubRegions.length) {
      results = [];

      const regionSetDetails = await this.projectService.regionDetails(
        this.projectId,
        resultRegion.regionSetId,
        this.projectRunConfigurationId,
        this.networkAssessmentAnalysisType);

      if (!regionSetDetails) {
        return undefined;
      }

      regionSetDetails.forEach((rsd) => {
        if (rsd && rsd.specialtyGroupSummaries) {
          rsd.specialtyGroupSummaries.forEach((sgs) => {
            const tempSpecialtyGroup = this.specialtyGroups.find((sg) => sg.code === sgs.specialtyGroup.code);
            const subRegionResult = granularSubRegions.find((sr) => sr.canonicalRegionSetId === rsd.regionSet.regionSetId);
            const tempRegionKey = keyFor(subRegionResult);
            const regionGroupKey = keyFor(subRegionResult, tempSpecialtyGroup);
            const details = buildAdequacyDetail(
              regionGroupKey,
              subRegionResult,
              sgs,
              tempSpecialtyGroup,
              this.regionSpecialtyGroupExceptions[regionGroupKey],
              aggregateDisplayType);

            let adequacyDetails = this.adequacyDetails[tempRegionKey];
            adequacyDetails = adequacyDetails
              ? adequacyDetails
              : {};
            adequacyDetails[regionGroupKey] = details;
            this.adequacyDetails[tempRegionKey] = adequacyDetails;

            results.push(adequacyDetailPsuedoClone(details, aggregateDisplayType));
          });
        }
      });
    }

    return results;
  }

  private async getAdequacyDetailsBySpecialtyInternal(
    resultRegion: ResultRegion,
    specialtyGroup: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    if (resultRegion.regionGranularity === RegionGranularity.County || !resultRegion.subRegions || resultRegion.subRegions.length === 0) {

      return await this.getCountyAdequacyDetailsBySpecialtyInternal(resultRegion, specialtyGroup, aggregateDisplayType);
    } else {
      return this.getParentRegionAdequacyDetailsBySpecialtyInternal(resultRegion, specialtyGroup, aggregateDisplayType);
    }
  }

  public clearAdequacyDetails(resultRegion: ResultRegion): void {
    const regionKey = keyFor(resultRegion);
    this.adequacyDetails[regionKey] = undefined;
  }

  private async getCountyAdequacyDetailsBySpecialtyInternal(
    resultRegion: ResultRegion,
    specialtyGroup: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    const regionKey = keyFor(resultRegion);
    const regionGroupKey = keyFor(resultRegion, specialtyGroup);
    let adequacyDetails = this.adequacyDetails[regionKey];
    let adequacyDetailsGroup = adequacyDetails
      ? adequacyDetails[regionGroupKey]
      : undefined;

    if (!adequacyDetails || !adequacyDetailsGroup) {

      adequacyDetails = adequacyDetails
        ? adequacyDetails
        : {};

      const regionSetDetails = await this.projectService.regionDetailsByRegionSet(
        this.projectId,
        resultRegion.regionSetId,
        this.projectRunConfigurationId,
        specialtyGroup.code,
        this.networkAssessmentAnalysisType);

      const adequacySummarySpecialtyGroup = (regionSetDetails && regionSetDetails[0] || undefined)
        && (regionSetDetails[0].specialtyGroupSummaries && regionSetDetails[0].specialtyGroupSummaries[0] || undefined)
        || undefined;

      if (!adequacySummarySpecialtyGroup) {
        return undefined;
      }

      adequacyDetailsGroup = buildAdequacyDetail(
        regionGroupKey,
        resultRegion,
        adequacySummarySpecialtyGroup,
        specialtyGroup,
        this.regionSpecialtyGroupExceptions[regionGroupKey],
        aggregateDisplayType);

      adequacyDetails[regionGroupKey] = adequacyDetailsGroup;

      this.adequacyDetails[regionKey] = adequacyDetails;
    }

    return [adequacyDetailPsuedoClone(adequacyDetailsGroup, aggregateDisplayType)];
  }

  private async getParentRegionAdequacyDetailsBySpecialtyInternal(
    resultRegion: ResultRegion,
    specialtyGroup: AnalysisSpecialtyGroup,
    aggregateDisplayType: AggregateDisplayType,
  ): Promise<AdequacyDetail[]> {
    let results: AdequacyDetail[] = [];

    const granularSubRegions = this.getGranularSubRegions(resultRegion, RegionGranularity.County);

    granularSubRegions.forEach((sr) => {
      const adequacyDetails = this.adequacyDetails[keyFor(sr)];

      const adequacyDetailsGroup = adequacyDetails
        ? adequacyDetails[keyFor(sr, specialtyGroup)]
        : undefined;
      if (adequacyDetailsGroup) {
        results.push(adequacyDetailPsuedoClone(adequacyDetailsGroup, aggregateDisplayType));
      }
    });

    if (results.length !== granularSubRegions.length) {
      results = [];
      const regionSetDetails = await this.projectService.regionDetailsByRegionSet(
        this.projectId,
        resultRegion.regionSetId,
        this.projectRunConfigurationId,
        specialtyGroup.code,
        this.networkAssessmentAnalysisType);

      if (!regionSetDetails) {
        return undefined;
      }

      regionSetDetails.forEach((rsd) => {
        const adequacySummarySpecialtyGroup = rsd.specialtyGroupSummaries && rsd.specialtyGroupSummaries[0] || undefined;
        if (adequacySummarySpecialtyGroup) {
          const subRegionResult = granularSubRegions.find((sr) => sr.canonicalRegionSetId === rsd.regionSet.regionSetId);
          const regionKey = keyFor(subRegionResult);
          const regionGroupKey = keyFor(subRegionResult, specialtyGroup);
          const details = buildAdequacyDetail(
            regionGroupKey,
            subRegionResult,
            adequacySummarySpecialtyGroup,
            specialtyGroup,
            this.regionSpecialtyGroupExceptions[regionGroupKey],
            aggregateDisplayType);

          let adequacyDetails = this.adequacyDetails[regionKey];
          adequacyDetails = adequacyDetails
            ? adequacyDetails
            : {};
          adequacyDetails[regionGroupKey] = details;
          this.adequacyDetails[regionKey] = adequacyDetails;

          results.push(adequacyDetailPsuedoClone(details, aggregateDisplayType));
        }
      });
    }

    return results;
  }

  private getGranularSubRegions(resultRegion: ResultRegion, targetGranularity: RegionGranularity): ResultRegion[] {
    let granularRegions: ResultRegion[] = [];

    if (resultRegion.regionGranularity === targetGranularity) {
      granularRegions.push(resultRegion);
    } else {
      resultRegion.subRegions.forEach((sr) => {
        granularRegions = granularRegions.concat(this.getGranularSubRegions(sr, targetGranularity));
      });
    }

    return granularRegions;
  }
}

/**
 * Creates a new, fully-initialized ProjectRunConfigurationResult
 * @param runConfigurationId
 * @param projectOptions
 * @param projectService
 * @param templateService
 * @param networkAssessmentAnalysisType
 */
async function createRunConfigurationResult(
  runConfigurationId: number,
  projectOptions: ProjectOptions,
  projectService: ProjectService,
  specialtyGroupExceptionsService: SpecialtyGroupExceptionsService,
  accuracyService: AccuracyService,
  attestationService: AttestationService,
  templateService: TemplateService,
  geographyService: GeographyService,
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
): Promise<ProjectRunConfigurationResult> {

  if (!runConfigurationId || !projectOptions) {
    return null;
  }

  const runConfiguration = projectOptions && projectOptions.projectRunConfigurations && projectOptions.projectRunConfigurations.length > 0
    ? projectOptions.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === runConfigurationId)
    : null;

  if (!runConfiguration) {
    return null;
  }

  let accuracyResults: AccuracyProjectResults = null;

  if (runConfiguration.accuracyTemplateId) {
    accuracyResults = await accuracyService.detailResults(projectOptions.projectId, runConfigurationId, networkAssessmentAnalysisType);
  }

  if (runConfiguration.accuracyTemplateId && !accuracyResults) {
    return null;
  }

  let adequacyResults: AdequacyProjectResults = null;

  if (runConfiguration.adequacyTemplateId) {
    adequacyResults = await projectService.adequacyDetailResults(projectOptions.projectId, runConfigurationId, networkAssessmentAnalysisType);
  }

  if (runConfiguration.adequacyTemplateId && !adequacyResults) {
    return null;
  }

  // TODO: Authorization.
  const attestationResults = await attestationService.detailResults(projectOptions.projectId, runConfigurationId);

  // We convert the project-run-configuration-service-area into a result-region
  // We give the service-area a special regionCode and force the regionGranularity here because
  // the service-area can/will change between run-configurations.  If we used the real values and
  // the service - area changed between runs we would be unable to join the service-area-level-results
  // (eg no-region-filters & viewing by specialty) correctly between run-configurations.
  const subRegions = adequacyResults ? [...adequacyResults.states] : [...accuracyResults.states];

  const serviceArea = {
    displayName: "",
    regionCode: "service-area",
    regionGranularity: RegionGranularity.Unknown,
    regionSetId: runConfiguration.serviceAreaRegionSetId,
    subRegions,
  } as ResultRegion;

  //we need to account for accuracy-only projects
  const specialtyGroups = adequacyResults && adequacyResults.serviceAreaAdequacySummary ?
    adequacyResults.specialtyGroups : accuracyResults && accuracyResults.specialtyGroups;

  // Then generate an accuracy, adequacy and attestation aggregates from aformentioned result-region
  const serviceAreaKey = keyFor(serviceArea);
  const accuracyAggregate = accuracyResults ? buildAccuracyAggregate(
    serviceAreaKey,
    serviceArea,
    accuracyResults.serviceAreaAccuracySummary,
    "unknown",
  ) : null;

  const adequacyAggregate = adequacyResults ? buildAdequacyAggregate(
    serviceAreaKey,
    serviceArea,
    adequacyResults.serviceAreaAdequacySummary,
    "unknown",
    undefined,
    undefined,
    adequacyResults.specialtyGroups?.some((sg) => sg.isExpandable),
  ) : null;

  const attestationAggregate = attestationResults ? buildAttestationAggregate(
    serviceAreaKey,
    serviceArea,
    attestationResults.serviceAreaAttestationSummary,
    "unknown",
  ) : null;

  // Use the full serviceArea, including all partial region sets, not just sub regions for this network assessment analysis type, in this to get the distinctRegionSetIds and generate the statuses
  const serviceAreaRegionSet = await geographyService.getRegionSet(runConfiguration.serviceAreaRegionSetId);
  const distinctRegionSetIds = getDistinctRegionSetIds(serviceAreaRegionSet);
  const statuses = (await projectService.getStatuses(projectOptions.projectId, distinctRegionSetIds, runConfiguration.projectRunConfigurationId))
    .reduce<{ [regionKey: string]: RegionSetStatusType }>((acc, status) => ({ ...acc, [keyFor(status)]: status.status }), {});
  const availableStatuses = (await projectService.getAvailableStatuses(projectOptions.projectId, runConfiguration.projectRunConfigurationId))
    .reduce<{ [regionKey: string]: AvailableRegionStatusesModel }>((acc, availableStatusModel) => ({ ...acc, [keyFor(availableStatusModel)]: availableStatusModel }), {});

  // Load specialty-group exceptions by region-set/code

  let exceptions: SpecialtyGroupException[] = null;
  if (runConfiguration.isProjected || runConfiguration.isTermed || runConfiguration.isProjectedTermed) {
    //projected and termed run configurations use a fake run configuration ID. Retrieve using just the project id
    //and let the API get the exceptions for the latest published run configuration
    //(or latest unpublished when no published exist) instead.
    exceptions = await specialtyGroupExceptionsService.getSpecialtyGroupExceptions(projectOptions.projectId);
  } else {
    exceptions = await specialtyGroupExceptionsService.getSpecialtyGroupExceptionsByRunConfigurationId(projectOptions.projectId,
      runConfiguration.projectRunConfigurationId);
  }

  const regionGroupExceptions = exceptions
    .reduce<{ [regionKey: string]: SpecialtyGroupException }>((acc, exc) => ({ ...acc, [keyFor(exc, exc.specialtyGroupCode)]: exc }), {});

  // Load the template used for the run configuration.
  const template = adequacyResults ? await templateService
    .getTemplateById(runConfiguration.adequacyTemplateCollectionId,
      runConfiguration.adequacyTemplateId, true) : null;

  //Build out a set of network types this run configuration uses
  const networkTypes: Set<NetworkType> = new Set<NetworkType>(["network"]);
  if (runConfiguration.prospectProviderSetId) {
    networkTypes.add("prospect");
  }
  if (runConfiguration.marketProviderSetId) {
    networkTypes.add("market");
  }

  return new ProjectRunConfigurationResultImpl(
    networkAssessmentAnalysisType,
    { [serviceAreaKey]: accuracyAggregate },
    { [serviceAreaKey]: adequacyAggregate },
    { [serviceAreaKey]: attestationAggregate },
    availableStatuses,
    statuses,
    regionGroupExceptions,
    serviceArea,
    specialtyGroups,
    projectOptions.projectId,
    projectService,
    specialtyGroupExceptionsService,
    accuracyService,
    attestationService,
    template,
    runConfiguration,
    networkTypes);
}

/** Determines the distinct set of region set identifiers contained within a result-region including itself. */
function getDistinctRegionSetIds(regionSet: RegionSet) {
  const res = [
    regionSet.regionSetId,
    ...regionSetIds(regionSet.partialRegionSets),
  ];

  return uniq(res).filter((x) => x !== null && x !== undefined);
}

/** Generates the key for the given result-region and optional specialty-group/specialty-group-code */
function keyFor(resultRegion: RegionSetQualifier, specialtyGroup?: AnalysisSpecialtyGroup | string, inspection?: string): string {
  let key = "";

  if (!!resultRegion) {
    switch (resultRegion.regionGranularity) {
      case RegionGranularity.CountyZip:
        key = `${resultRegion.regionGranularity}:${resultRegion.regionCode}:${resultRegion.subRegionCode}`;
        break;
      case RegionGranularity.MultiRegion:
      case RegionGranularity.Unknown:
        //we made the decision to switch the MultiRegion top-level service area to unknown in the UI and
        //we need to be consistent with that
        key = `${RegionGranularity.Unknown}:${resultRegion.regionSetId}`;
        break;
      default:
        key = `${resultRegion.regionGranularity}:${resultRegion.regionCode}`;
    }
  }

  if (!!specialtyGroup && !!resultRegion) {
    if (typeof specialtyGroup === "string") {
      //if the region set is multi-region the key is just the specialty group
      //otherwise compare mode repeats the specialty groups if the region set changed between run configurations
      key = resultRegion.regionGranularity === RegionGranularity.MultiRegion || resultRegion.regionGranularity === RegionGranularity.Unknown ? specialtyGroup : `${key}:${specialtyGroup}`;
    } else {
      key = resultRegion.regionGranularity === RegionGranularity.MultiRegion || resultRegion.regionGranularity === RegionGranularity.Unknown ? specialtyGroup.code : `${key}:${specialtyGroup.code}`;
    }
  }
  if (!!inspection) {
    key = `${key}:${inspection}`;
  }

  return key;
}

/** Determines the non-distinct set of region sets from a list of region sets. */
function regionSetIds(regionSet: RegionSet[]) {
  let res: number[] = [];
  for (const r of regionSet) {
    res = [...res, r.regionSetId, ...regionSetIds(r.partialRegionSets || [])];
  }

  return res;
}

/**
 * Transforms the aggregates into a flattened array of records
 * @param aggregates The aggregates in question.
 */
function transformAggregates(aggregates: AccuracyAggregateInspectionResultGroup[]) {
  const records = flatMap(aggregates, (aggregate) => {
    return aggregate.results.map((result) => ({
      groupName: aggregate.groupName,
      inspectionGroupUid: aggregate.inspectionGroupUid,
      ...result,
    }));
  });

  return records;
}

export { createRunConfigurationResult, ProjectRunConfigurationResult };
